import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Form, Modal } from 'react-bootstrap';
import PageHeader from '../../components/PageHeader';
import { Icon } from '../../components/icons';
import { formatDate, formatMoney } from '../../utils/format';

interface Event {
    id: number;
    name: string;
    amount: number;
    dateAdded: string;
    schoolYearId: number;
}

interface EventsManagerProps {
    schoolYearId: number;
}

const EventsManager: React.FC<EventsManagerProps> = ({ schoolYearId }) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
    const [eventToDeleteId, setEventToDeleteId] = useState<number | null>(null);
    const [isNew, setIsNew] = useState(false);

    useEffect(() => {
        if (schoolYearId) {
            axios.get<Event[]>(`/api/events/school-year/${schoolYearId}`)
                .then(response => setEvents(response.data))
                .catch(error => console.error("Error fetching events:", error));
        }
    }, [schoolYearId]);

    const handleSave = (event: Event) => {
        if (isNew) {
            axios.post<Event>('/api/events', { ...event, schoolYearId })
                .then(response => {
                    setEvents([...events, { ...response.data, schoolYearId }]);
                    setShowModal(false);
                })
                .catch(error => console.error("Error creating event:", error));
        } else {
            axios.put<Event>(`/api/events/${event.id}`, { ...event, schoolYearId })
                .then(response => {
                    setEvents(events.map(e => e.id === event.id ? { ...response.data, schoolYearId } : e));
                    setShowModal(false);
                })
                .catch(error => console.error("Error updating event:", error));
        }
    };

    const handleDelete = (id: number) => {
        axios.delete(`/api/events/${id}`)
            .then(() => {
                setEvents(events.filter(event => event.id !== id));
                setShowConfirmDeleteModal(false);
            })
            .catch(error => console.error("Error deleting event:", error));
    };

    const openModal = (event: Event | null) => {
        setIsNew(!event);
        setCurrentEvent(event || { id: 0, name: '', amount: 0, dateAdded: new Date().toISOString().split('T')[0], schoolYearId });
        setShowModal(true);
    };

    const openConfirmDeleteModal = (id: number) => {
        setEventToDeleteId(id);
        setShowConfirmDeleteModal(true);
    };

    const eventToDelete = events.find(e => e.id === eventToDeleteId);
    const canSave = !!currentEvent && currentEvent.name.trim() !== '' && !isNaN(currentEvent.amount) && currentEvent.amount >= 0;

    return (
        <>
            <PageHeader
                title="Wydarzenia"
                subtitle="Składki i wydarzenia, za które zbierane są wpłaty w tym roku szkolnym"
                actions={
                    <Button onClick={() => openModal(null)}>
                        <Icon name="plus" />
                        Dodaj wydarzenie
                    </Button>
                }
            />

            {events.length === 0 ? (
                <div className="surface empty-state">Nie ma jeszcze żadnych wydarzeń w tym roku szkolnym.</div>
            ) : (
                <div className="surface table-card">
                    <div className="table-scroll">
                        <table className="table data-table table-stack">
                            <thead>
                                <tr>
                                    <th scope="col">Nazwa</th>
                                    <th className="num" scope="col">Kwota</th>
                                    <th scope="col">Data dodania</th>
                                    <th className="actions" scope="col"><span className="visually-hidden">Akcje</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map(event => (
                                    <tr key={event.id}>
                                        <td data-label="Nazwa" className="fw-semibold">{event.name}</td>
                                        <td data-label="Kwota" className="num">{formatMoney(event.amount)}</td>
                                        <td data-label="Data dodania" className="nowrap">{formatDate(event.dateAdded)}</td>
                                        <td data-label="" className="actions">
                                            <div className="d-inline-flex gap-2">
                                                <Button size="sm" variant="outline-secondary" onClick={() => openModal(event)}>
                                                    <Icon name="pencil" size={15} />
                                                    Edytuj
                                                </Button>
                                                <Button size="sm" variant="outline-danger" onClick={() => openConfirmDeleteModal(event.id)}>
                                                    <Icon name="trash" size={15} />
                                                    Usuń
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Form onSubmit={(e) => { e.preventDefault(); if (canSave) handleSave(currentEvent!); }}>
                    <Modal.Header closeButton>
                        <Modal.Title>{isNew ? 'Dodaj wydarzenie' : 'Edytuj wydarzenie'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3" controlId="event-name">
                            <Form.Label>Nazwa</Form.Label>
                            <Form.Control
                                type="text"
                                autoFocus
                                value={currentEvent?.name || ''}
                                onChange={(e) => setCurrentEvent({ ...currentEvent!, name: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="event-amount">
                            <Form.Label>Kwota (zł)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={Number.isNaN(currentEvent?.amount) ? '' : currentEvent?.amount ?? 0}
                                onChange={(e) => setCurrentEvent({ ...currentEvent!, amount: parseFloat(e.target.value) })}
                            />
                        </Form.Group>
                        <Form.Group controlId="event-date">
                            <Form.Label>Data dodania</Form.Label>
                            <Form.Control
                                type="date"
                                value={currentEvent?.dateAdded || ''}
                                onChange={(e) => setCurrentEvent({ ...currentEvent!, dateAdded: e.target.value })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Anuluj</Button>
                        <Button variant="primary" type="submit" disabled={!canSave}>Zapisz</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showConfirmDeleteModal} onHide={() => setShowConfirmDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Potwierdź usunięcie</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Czy na pewno chcesz usunąć wydarzenie{eventToDelete ? <> <strong>„{eventToDelete.name}”</strong></> : ' '}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmDeleteModal(false)}>Anuluj</Button>
                    <Button variant="danger" onClick={() => handleDelete(eventToDeleteId!)}>Usuń</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EventsManager;
